import {
  MatchingInviteRespondType,
  Patient,
  PatientInviteType,
  PatientListItem,
  RelationType,
  Survey,
  SurveyBody,
  acceptMatchingInvite,
  addSurveyData,
  checkSuperRelative,
  createPatientSuggestion,
  createSurveyData,
  declineMatchingInvite,
  getGlossaryTerms,
  getPatient,
  getPatientInvite,
  getSurveyData,
  listPatientSuggestions,
  listPatients,
  queryPatientByBirthdate,
  queryPatientByFirstName,
  queryPatientByLastName,
  removePatient,
  resendPatientInvite,
  submitOnboarding,
  withdrawPatientSuggestion,
} from '@/api'
import { useAuthContext } from '@/providers'
import {
  ReactQueryKey,
  ReactQueryOptions,
  reactQueryKeys,
  useInvalidateOnSuccessMutation,
  useMutation,
} from '@/utils'
import { AxiosError } from 'axios'
import { or, pipe, prop } from 'ramda'
import { useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useParams } from 'react-router-dom'

export const usePatientsList = () =>
  useQuery(reactQueryKeys.patients, listPatients, {
    // Die App laeuft in einer langlebigen WebView-Session. Ohne Refetch wuerde
    // eine Wiederaufnahme (active: false -> true) erst nach einem Reload sichtbar.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  })

/**
 * Invalidiert patientenbezogene Caches, wenn ein entlassener Patient wieder
 * aufgenommen wurde (active: false -> true). Ohne das blieben Chat-Polling,
 * Upload-Buttons, Textbausteine und Tagebuch im Read-only-Zustand haengen.
 */
export const useInvalidateOnPatientReactivation = (patients: PatientListItem[]) => {
  const queryClient = useQueryClient()
  const previousActiveById = useRef<Record<number, boolean>>({})

  useEffect(() => {
    patients.forEach((patient) => {
      const wasActive = previousActiveById.current[patient.id]
      if (wasActive === false && patient.active) {
        const id = String(patient.id)
        const textElementsKey: ReactQueryKey = [reactQueryKeys.textElements, id]
        // 8h-Fenster des Textbausteine-Caches zuruecksetzen (useCachedQuery)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        delete (window as any)[JSON.stringify(textElementsKey)]
        queryClient.invalidateQueries([reactQueryKeys.patients, id])
        queryClient.invalidateQueries([reactQueryKeys.chat, id])
        queryClient.invalidateQueries(reactQueryKeys.chatMessages)
        queryClient.invalidateQueries(textElementsKey)
        queryClient.invalidateQueries([reactQueryKeys.pinnedMessages, id])
        queryClient.invalidateQueries(reactQueryKeys.documentTypes)
        queryClient.invalidateQueries(reactQueryKeys.icuDiary)
      }
      previousActiveById.current[patient.id] = patient.active
    })
  }, [patients, queryClient])
}

export const usePatient = <T>(
  patientId: string,
  options: ReactQueryOptions<Patient, ['patients', string], T> = {},
) =>
  useQuery([reactQueryKeys.patients, patientId], () => getPatient(patientId as string), {
    enabled: Boolean(patientId),
    ...options,
  })

export const useCurrentPatient = <T = Patient>(
  options?: ReactQueryOptions<Patient, ['patients', string], T>,
) => {
  const { patientId } = useParams()

  return usePatient(patientId!, options)
}

export const useCheckSuperRelative = (
  patientId?: number,
  options?: ReactQueryOptions<{ super_relative: boolean }, ['superRelative', string], boolean>,
) =>
  useQuery(
    [reactQueryKeys.superRelative, String(patientId)],
    () => checkSuperRelative(String(patientId)),
    options,
  )

export const useCheckCurrentSuperRelative = () => {
  const { currentPatient } = useAuthContext()

  return useCheckSuperRelative(currentPatient?.id, {
    enabled: Boolean(currentPatient?.id && currentPatient.connection_status === 'assigned'),
    select: pipe(prop('super_relative'), or(currentPatient?.is_patient_user)),
  })
}

export const useGlossaryTerms = (patientId: string) =>
  useQuery(['glossaryTerms', patientId], () => getGlossaryTerms(patientId), {
    enabled: Boolean(patientId),
  })

export const useQueryPatientByBirthdate = () => useMutation(queryPatientByBirthdate)
export const useQueryPatientByLastName = () => useMutation(queryPatientByLastName)
export const useQueryPatientByFirstName = () => useMutation(queryPatientByFirstName)

export const useSubmitOnboarding = () =>
  useInvalidateOnSuccessMutation('patients', submitOnboarding, true)

/**
 * Offene und beantwortete Patientenvorschlaege des/der Angehoerigen. Wird auch waehrend
 * des Wartens gepollt, damit ein von der Station uebernommener Vorschlag ohne Reload
 * verschwindet - der/die Patient:in taucht dann in der Patientenliste auf.
 */
export const usePatientSuggestionsList = () =>
  useQuery(reactQueryKeys.patientSuggestions, listPatientSuggestions, {
    select: prop('results'),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  })

export const useCreatePatientSuggestion = () =>
  useInvalidateOnSuccessMutation('patientSuggestions', createPatientSuggestion)

export const useWithdrawPatientSuggestion = () =>
  useInvalidateOnSuccessMutation('patientSuggestions', (suggestionId: string) =>
    withdrawPatientSuggestion(suggestionId),
  )

export const useAcceptMatchingInvite = (params: MatchingInviteRespondType) =>
  useInvalidateOnSuccessMutation(
    'patients',
    ({ relation }: { relation?: RelationType }) => acceptMatchingInvite({ ...params, relation }),
    true,
  )

export const useDeclineMatchingInvite = (params: MatchingInviteRespondType) =>
  useInvalidateOnSuccessMutation('patients', () => declineMatchingInvite(params), true)

export const useRemovePatient = (patientId: number) =>
  useInvalidateOnSuccessMutation('patients', () => removePatient(patientId), true)

export const useQueryPatientSurvey = (patientId?: number) =>
  useQuery<Survey, AxiosError<{ first_name: string; last_name: string }>>(
    [reactQueryKeys.survey, String(patientId)],
    () => getSurveyData(patientId!),
    {
      enabled: Boolean(patientId),
    },
  )

export const useCreatePatientSurvey = (patientId: number) =>
  useInvalidateOnSuccessMutation(['survey', String(patientId)], (body: SurveyBody) =>
    createSurveyData(patientId, body),
  )

export const useUpdatePatientSurvey = (patientId: number, surveyId: string) =>
  useMutation((body: SurveyBody) => addSurveyData(patientId, surveyId, body))

export const usePatientInvite = (
  patientId: number,
  options?: ReactQueryOptions<PatientInviteType, ['patientInvite', string]>,
) =>
  useQuery(
    [reactQueryKeys.patientInvite, String(patientId)],
    () => getPatientInvite(patientId),
    options,
  )

export const useResendPatientInvite = (patientId: number) =>
  useInvalidateOnSuccessMutation(['patientInvite', String(patientId)], () =>
    resendPatientInvite(patientId),
  )
